import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpEventType,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { map, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { ErrorHandlingService } from './error-handling.service';
import { ApiResponse } from '../models/api-response.models';

export interface UploadProgressEvent {
  status: 'progress';
  percentage: number;
}

export interface UploadCompletedEvent<T = unknown> {
  status: 'completed';
  percentage: 100;
  response: {
    result: T | null;
    success: boolean;
  };
}

export interface UploadStartEvent {
  status: 'start';
  percentage: 0;
}

export type UploadEvent<T = unknown> =
  | UploadProgressEvent
  | UploadCompletedEvent<T>
  | UploadStartEvent;

@Injectable({
  providedIn: 'root',
})
export class UploadProgressService {
  private readonly MAX_PROGRESS = 80;

  constructor(private errorHandlingService: ErrorHandlingService) {}

  private calculateProgress(loaded: number, total: number): number {
    const percentage = total ? Math.round((100 * loaded) / total) : 0;
    return Math.min(Math.round(percentage * 0.8), this.MAX_PROGRESS);
  }

  private handleTimeoutError(): Observable<UploadEvent<never>> {
    return of({
      status: 'progress',
      percentage: this.MAX_PROGRESS,
    });
  }

  private mapHttpEvent<T>(event: HttpEvent<ApiResponse<T>>): UploadEvent<T> {
    switch (event.type) {
      case HttpEventType.UploadProgress: {
        return {
          status: 'progress',
          percentage: this.calculateProgress(event.loaded, event.total ?? 0),
        };
      }

      case HttpEventType.Response:
        return {
          status: 'completed',
          percentage: 100,
          response: {
            result:
              (event as HttpResponse<ApiResponse<T>>).body?.result ?? null,
            success: true,
          },
        };

      default:
        return {
          status: 'start',
          percentage: 0,
        };
    }
  }

  getUploadProgress<T>() {
    return (
      source: Observable<HttpEvent<ApiResponse<T>>>
    ): Observable<UploadEvent<T>> =>
      source.pipe(
        map(
          (event: HttpEvent<ApiResponse<T>>): UploadEvent<T> =>
            this.mapHttpEvent(event)
        ),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 504) {
            return this.handleTimeoutError();
          }
          return this.errorHandlingService.handleError(error);
        })
      );
  }
}
